import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Wrench, PlayCircle, RefreshCw, History, CheckCircle2, XCircle,
  Loader2, AlertTriangle, Zap, Terminal, Star, Trash2, Plus, ChevronDown, ChevronUp, Info,
  Wand2, ScrollText,
} from 'lucide-react';

// iter-377 — issue categories the Reconciliation Fix flow knows how to act on.
const FR_FIXABLE_ISSUES = ['missing_from_invoice', 'invoiced_in_wrong_month', 'commission_amount_mismatch', 'commission_rate_error_at_settlement'];
const FR_RECON_TEMPLATE_ID = 'finance-register-vs-commission-invoice-reconciliation';

const StatusBadge = ({ status, summary }) => {
  // iter-230bm — When a run completed but had per-row failures, show
  // "Partial" instead of misleading "Succeeded". `summary` carries the
  // `failed` / `applied` counts from the executor.
  const failedCount = Number(summary?.failed || 0);
  const appliedCount = Number(summary?.applied || 0);
  const skippedCount = Number(summary?.skipped_missing_data || 0);
  let effectiveStatus = status;
  if (status === 'succeeded' && (failedCount > 0 || (skippedCount > 0 && appliedCount === 0))) {
    effectiveStatus = 'partial';
  }
  const map = {
    succeeded: { label: 'Succeeded', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
    partial:   { label: 'Partial',   cls: 'bg-amber-50 text-amber-700 border-amber-200',       Icon: AlertTriangle },
    failed:    { label: 'Failed',    cls: 'bg-rose-50 text-rose-700 border-rose-200',          Icon: XCircle },
    running:   { label: 'Running',   cls: 'bg-amber-50 text-amber-700 border-amber-200',       Icon: Loader2 },
  };
  const m = map[effectiveStatus] || { label: status, cls: 'bg-gray-50 text-gray-600 border-gray-200', Icon: History };
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}>
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {m.label}
    </span>
  );
};

// iter-230bm — Format ISO timestamps as IST since the entire ops team
// works out of India. Server stores everything in UTC; converting at
// the edge is cleanest.
const formatISTTimestamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    // toLocaleString respects the Asia/Kolkata zone offset (+5:30)
    const formatted = d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    return `${formatted} IST`;
  } catch (e) {
    return iso;
  }
};

// iter-230q — visual differentiator so admins immediately see what
// kind of risk they're about to take.
const CategoryBadge = ({ category }) => {
  const map = {
    read_only: { label: 'Read-only', cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: History },
    mutating: { label: 'Mutating', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle },
    async_batch: { label: 'Async batch', cls: 'bg-purple-50 text-purple-700 border-purple-200', Icon: Zap },
  };
  const m = map[category] || { label: category, cls: 'bg-gray-50 text-gray-600 border-gray-200', Icon: History };
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${m.cls}`}>
      <Icon className="h-3 w-3" />
      {m.label}
    </span>
  );
};

// iter-380 — read-only helper for diagnosing "404: No completed payouts
// with commission found..." when a Reconciliation Fix (or manual invoice
// generation) unexpectedly fails for an order that visibly has a
// completed payout. Independent of any template — always available.
function DiagnoseOrderPayoutCard({ apiCall }) {
  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!orderId.trim() && !orderNumber.trim()) {
      toast.error('Enter an order_id or order number');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const qs = orderId.trim()
        ? `order_id=${encodeURIComponent(orderId.trim())}`
        : `order_number=${encodeURIComponent(orderNumber.trim())}`;
      const res = await apiCall(`/api/admin/commission-invoices/audit/diagnose-payout-eligibility?${qs}`);
      setResult(res);
    } catch (e) {
      toast.error('Diagnosis failed', { description: e?.message || String(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="diagnose-order-payout-card">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setOpen((o) => !o)}>
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Terminal className="h-4 w-4 text-indigo-600" />
            Diagnose "No completed payouts found" error
          </span>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </CardTitle>
        <CardDescription>
          Enter the order_id or order number that failed to invoice — this replays the exact
          eligibility check (status / date bucket / commission / type) against its raw stored
          payout row and tells you which one is failing.
        </CardDescription>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <input
              className="flex-1 min-w-[200px] text-sm border border-gray-200 rounded px-3 py-2"
              placeholder="order_id (e.g. an internal UUID)"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              data-testid="diagnose-order-id-input"
            />
            <input
              className="flex-1 min-w-[200px] text-sm border border-gray-200 rounded px-3 py-2"
              placeholder="or order number (e.g. #1026-A)"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              data-testid="diagnose-order-number-input"
            />
            <Button onClick={run} disabled={loading} data-testid="diagnose-order-run-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Diagnose
            </Button>
          </div>
          {result && !result.ok && (
            <div className="text-sm text-red-600" data-testid="diagnose-order-not-found">
              {result.outcome === 'no_vendor_payouts_row_found_at_all'
                ? 'No vendor_payouts row exists at all for this order — the payout was never generated.'
                : 'Diagnosis failed.'}
            </div>
          )}
          {result?.ok && (result.rows || []).map((r, i) => (
            <div
              key={i}
              className={`rounded-md border p-3 text-xs space-y-1 ${r.would_be_included ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
              data-testid={`diagnose-order-result-${i}`}
            >
              <div className="font-semibold">
                {r.would_be_included ? 'Would be included ✓' : 'EXCLUDED — this is why the fix/invoice failed'}
              </div>
              <div>vendor_id: <span className="font-mono">{r.vendor_id}</span> · resolved bucket month: <span className="font-mono">{r.resolved_bucket_month || '—'}</span></div>
              <div>order_status: <span className="font-mono">{JSON.stringify(r.order_status_raw)}</span> ({r.status_ok ? 'ok' : 'FAILS'})</div>
              <div>order_created_at: <span className="font-mono">{r.order_created_at_raw || '—'}</span> · created_at: <span className="font-mono">{r.created_at_raw || '—'}</span> ({r.date_ok ? 'ok' : 'FAILS'})</div>
              <div>commission: <span className="font-mono">{r.commission}</span> ({r.commission_ok ? 'ok' : 'FAILS'}) · type: <span className="font-mono">{r.type}</span> ({r.type_ok ? 'ok' : 'FAILS'})</div>
              {r.failure_reasons && (
                <div className="text-red-700 font-medium pt-1">{r.failure_reasons.join('; ')}</div>
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}


export default function MaintenanceRunner() {
  const { apiCall } = useAuth();

  // ─── Template mode state ─────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [params, setParams] = useState({});
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  // ─── Execution source (Template vs Custom Command) ───────────────────
  const [execSource, setExecSource] = useState('template'); // 'template' | 'custom'

  // ─── Custom command state ────────────────────────────────────────────
  const [cmdMethod, setCmdMethod] = useState('GET');
  const [cmdEndpoint, setCmdEndpoint] = useState('');
  const [cmdBody, setCmdBody] = useState('');
  const [cmdExecMode, setCmdExecMode] = useState('preview'); // 'preview' | 'dry_run' | 'execute'
  const [cmdBodyError, setCmdBodyError] = useState('');

  // ─── Saved commands (localStorage-backed shortcuts) ──────────────────
  const [savedCmds, setSavedCmds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('maint_saved_cmds') || '[]');
    } catch {
      return [];
    }
  });
  const [savedOpen, setSavedOpen] = useState(false);

  // ─── Confirmation modal (shared: template + custom execute mode) ─────
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  // ─── Async polling ───────────────────────────────────────────────────
  const [activeAsyncRun, setActiveAsyncRun] = useState(null);
  const pollTimerRef = useRef(null);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId]
  );

  // ─── Registry + history load ────────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await apiCall('/api/admin/maintenance/templates');
      setTemplates(res?.templates || []);
    } catch (e) {
      toast.error('Could not load template registry', { description: e?.message || String(e) });
    }
  }, [apiCall]);

  const fetchRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const res = await apiCall('/api/admin/maintenance/runs?limit=20');
      setRecentRuns(res?.runs || []);
    } catch (e) {
      // non-fatal — the runs panel is auxiliary
    } finally {
      setLoadingRuns(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchTemplates();
    fetchRuns();
  }, [fetchTemplates, fetchRuns]);

  // Pick the first template by default once the registry arrives.
  useEffect(() => {
    if (!selectedId && templates.length) {
      setSelectedId(templates[0].id);
    }
  }, [templates, selectedId]);

  // ─── Async polling helpers (declared FIRST so subsequent effects can use them) ───
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // When the template changes, reseed param defaults AND clear any
  // active async run state. The `selectedId` dep is explicit (in
  // addition to `selectedTemplate`) so a stale template-object
  // reference can't suppress the reseed.
  // iter-230q (post-test) — also resets `dry_run` to the schema
  // default. Without this, toggling dry_run=false on template A and
  // then picking template B carried the OFF state over, opening the
  // CONFIRM modal on B unexpectedly.
  useEffect(() => {
    if (selectedTemplate) {
      setParams(_seedParams(selectedTemplate));
      stopPolling();
      setActiveAsyncRun(null);
    }
  }, [selectedTemplate, selectedId, stopPolling]);

  const pollRun = useCallback(async (runId, templateName) => {
    try {
      const row = await apiCall(`/api/admin/maintenance/runs/${runId}`);
      setActiveAsyncRun({ run_id: runId, ...(row || {}) });
      if (row && (row.status === 'succeeded' || row.status === 'failed')) {
        stopPolling();
        setRunning(false);
        // Build a sync-shaped result so the "Last run" card renders the
        // same way as Phase 1 / 2 results.
        // iter-230be — Async runs now snapshot up to 100 rows into
        // `result_summary.error_rows`, so populate the result.rows so
        // the table can render them. This is the difference between
        // "No rows returned" (old) and a usable error inventory (new).
        const errorRows = Array.isArray(row?.result_summary?.error_rows)
          ? row.result_summary.error_rows
          : [];
        setLastResult({
          run_id: runId,
          status: row.status,
          summary: row.result_summary,
          result: { rows: errorRows },
          _template_name: templateName,
        });
        if (row.status === 'succeeded') {
          toast.success(`${templateName} — succeeded`, {
            description: _summaryLine(row.result_summary),
          });
        } else {
          toast.error(`${templateName} — failed`, {
            description: row.error || 'See run details for the stack trace.',
          });
        }
        fetchRuns();
      }
    } catch (_e) {
      // Transient — keep polling.
    }
  }, [apiCall, stopPolling, fetchRuns]);

  // Cleanup poll timer on unmount.
  useEffect(() => () => stopPolling(), [stopPolling]);

  // ─── Run action ─────────────────────────────────────────────────────
  const executeRun = useCallback(async () => {
    if (!selectedTemplate) return;
    stopPolling();
    setRunning(true);
    setLastResult(null);
    setActiveAsyncRun(null);
    try {
      const res = await apiCall(
        `/api/admin/maintenance/templates/${selectedTemplate.id}/run`,
        { method: 'POST', body: JSON.stringify({ params }) }
      );
      if (res?.async) {
        // Phase 3 — start polling at 3s tick.
        const tplName = selectedTemplate.name;
        // iter-230q (post-test) — Render the progress card OPTIMISTICALLY
        // with a synthetic `status:'running'` so the user sees the card
        // immediately, even on small datasets where the executor
        // finishes before the first 3s poll lands. The first poll
        // overwrites this with real progress data.
        setActiveAsyncRun({
          run_id: res.run_id,
          status: 'running',
          progress: { processed: 0, total: 0, percent: 0 },
          template_name: tplName,
        });
        // Immediate poll so the UI shows initial progress fast,
        // then 3s tick.
        pollRun(res.run_id, tplName);
        pollTimerRef.current = setInterval(() => pollRun(res.run_id, tplName), 3000);
        toast.message(`${tplName} — dispatched`, {
          description: 'Async batch running in background. Live progress below.',
        });
        fetchRuns();
        // Don't flip setRunning(false) — keep the button disabled until poll completes.
        return;
      }
      // Sync (Phase 1 / 2).
      setLastResult({ ...res, _template_name: selectedTemplate.name, _template_id: selectedTemplate.id });
      toast.success(`${selectedTemplate.name} — ${res?.status || 'done'}`, {
        description: _summaryLine(res?.summary),
      });
      fetchRuns();
    } catch (e) {
      const detail = e?.detail || e?.response?.data?.detail || e?.message;
      const msg = typeof detail === 'object' ? detail.message || JSON.stringify(detail) : detail;
      toast.error('Run failed', { description: msg || 'Unknown error' });
      setRunning(false);
    } finally {
      // Only release the lock for sync runs. Async runs release on poll completion.
      // (No-op here — handled above.)
    }
  }, [apiCall, params, pollRun, selectedTemplate, stopPolling, fetchRuns]);

  const handleRun = () => {
    // ─── Custom command mode ─────────────────────────────────────────
    if (execSource === 'custom') {
      // Validate endpoint
      if (!cmdEndpoint.trim().startsWith('/api/')) {
        toast.error('Invalid endpoint', { description: 'Endpoint must start with /api/' });
        return;
      }
      // Validate JSON body
      if (cmdBody.trim() && cmdMethod !== 'GET') {
        try { JSON.parse(cmdBody); setCmdBodyError(''); } catch (e) {
          setCmdBodyError(`Invalid JSON: ${e.message}`);
          toast.error('Invalid JSON in request body', { description: e.message });
          return;
        }
      }
      // Execute mode requires confirmation
      if (cmdExecMode === 'execute') {
        setConfirmText('');
        setConfirmOpen(true);
        return;
      }
      executeCustomRun();
      return;
    }
    // ─── Template mode ───────────────────────────────────────────────
    if (!selectedTemplate) return;
    const isMutating = selectedTemplate.category === 'mutating';
    const isAsync = selectedTemplate.category === 'async_batch';
    const wantsApply = (isMutating || isAsync) && params?.dry_run === false;
    if (wantsApply) {
      setConfirmText('');
      setConfirmOpen(true);
      return;
    }
    executeRun();
  };

  // ─── Saved commands helpers ──────────────────────────────────────────
  const persistSaved = (next) => {
    setSavedCmds(next);
    localStorage.setItem('maint_saved_cmds', JSON.stringify(next));
  };

  const saveCurrentCmd = () => {
    if (!cmdEndpoint.trim()) { toast.error('Enter an endpoint first'); return; }
    const entry = {
      id: Date.now().toString(),
      label: `${cmdMethod} ${cmdEndpoint}`,
      method: cmdMethod,
      endpoint: cmdEndpoint.trim(),
      body: cmdBody,
    };
    const next = [entry, ...savedCmds.filter((c) => c.endpoint !== entry.endpoint || c.method !== entry.method)].slice(0, 12);
    persistSaved(next);
    toast.success('Command saved');
  };

  const loadSavedCmd = (cmd) => {
    setCmdMethod(cmd.method);
    setCmdEndpoint(cmd.endpoint);
    setCmdBody(cmd.body || '');
    setExecSource('custom');
    setSavedOpen(false);
  };

  const deleteSavedCmd = (id) => {
    persistSaved(savedCmds.filter((c) => c.id !== id));
  };

  // ─── Custom command executor ────────────────────────────────────────
  const executeCustomRun = useCallback(async () => {
    stopPolling();
    setRunning(true);
    setLastResult(null);
    setActiveAsyncRun(null);

    let parsedBody = {};
    if (cmdBody.trim() && cmdMethod !== 'GET') {
      try { parsedBody = JSON.parse(cmdBody); } catch { /* already validated */ }
    }

    // Map exec mode to dry_run
    const dryRunMap = { preview: true, dry_run: true, execute: false };
    const dry_run = cmdMethod === 'GET' ? null : dryRunMap[cmdExecMode];
    const label = `${cmdMethod} ${cmdEndpoint}`;

    try {
      const res = await apiCall('/api/admin/maintenance/custom/run', {
        method: 'POST',
        body: JSON.stringify({
          method: cmdMethod,
          endpoint: cmdEndpoint,
          body: parsedBody,
          label,
          dry_run,
        }),
      });
      setLastResult({ ...res, _template_name: label });
      toast.success(`${label} — ${res?.status || 'done'}`, {
        description: _summaryLine(res?.summary),
      });
      fetchRuns();
    } catch (e) {
      const detail = e?.detail || e?.response?.data?.detail || e?.message;
      const msg = typeof detail === 'object' ? detail.message || JSON.stringify(detail) : detail;
      toast.error('Custom command failed', { description: msg || 'Unknown error' });
    } finally {
      setRunning(false);
    }
  }, [apiCall, cmdBody, cmdEndpoint, cmdExecMode, cmdMethod, fetchRuns, stopPolling]);

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6" data-testid="maintenance-runner-page">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Wrench className="h-6 w-6 text-amber-600" /> Maintenance Runner
              <Badge variant="outline" className="text-[10px] tracking-wider uppercase">v2 · Custom Commands</Badge>
            </h1>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Use <strong>Templates</strong> for common recurring jobs or <strong>Custom Commands</strong> to invoke any registered backend API on demand. Every run is audit-logged to <code>command_runs</code>.
            </p>
          </div>
          <Button
            onClick={() => { fetchTemplates(); fetchRuns(); }}
            variant="outline"
            size="sm"
            data-testid="maintenance-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </header>

        <DiagnoseOrderPayoutCard apiCall={apiCall} />

        {/* ── Main operation card ──────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Run an operation</CardTitle>
            <CardDescription>
              Choose a predefined template or build a custom API command.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">

            {/* ── Execution Source Toggle ──────────────────────────── */}
            <div
              className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 gap-1"
              data-testid="exec-source-toggle"
            >
              <button
                onClick={() => setExecSource('template')}
                data-testid="exec-source-template-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  execSource === 'template'
                    ? 'bg-white shadow-sm text-amber-700 border border-amber-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Wrench className="h-3.5 w-3.5" /> Template
              </button>
              <button
                onClick={() => setExecSource('custom')}
                data-testid="exec-source-custom-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  execSource === 'custom'
                    ? 'bg-white shadow-sm text-indigo-700 border border-indigo-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Terminal className="h-3.5 w-3.5" /> Custom Command
              </button>
            </div>

            {/* ── Template Mode ────────────────────────────────────── */}
            {execSource === 'template' && (
            <div className="space-y-4">
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Template</label>
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger data-testid="maintenance-template-select" className="w-full">
                    <SelectValue placeholder="Choose a template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="inline-flex items-center gap-2">
                          <CategoryBadge category={t.category} />
                          <span>{t.name}</span>
                          <span className="text-xs text-gray-400">({t.id})</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleRun}
                disabled={!selectedTemplate || running}
                data-testid="maintenance-run-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
                {running ? 'Running…' : 'Run'}
              </Button>
            </div>

            {selectedTemplate && (
              <div className={`rounded-md border p-3 text-sm space-y-2 ${
                selectedTemplate.category === 'read_only'
                  ? 'bg-blue-50/50 border-blue-100'
                  : selectedTemplate.category === 'mutating'
                    ? 'bg-amber-50/50 border-amber-200'
                    : 'bg-purple-50/50 border-purple-200'
              }`}>
                <div className="flex items-center gap-2">
                  <CategoryBadge category={selectedTemplate.category} />
                  {selectedTemplate.requires_dry_run && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded">
                      dry_run safety
                    </span>
                  )}
                  {selectedTemplate.supports_progress && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-purple-700 bg-purple-100 border border-purple-200 px-2 py-0.5 rounded">
                      live progress
                    </span>
                  )}
                </div>
                <p className="text-gray-700">{selectedTemplate.description}</p>
                {(selectedTemplate.category === 'mutating' || selectedTemplate.category === 'async_batch') && (
                  <p className="text-xs text-amber-800 flex items-start gap-1">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      This template writes to the database. The <code>dry_run</code> toggle is ON by default —
                      switch it OFF and confirm via the modal to apply changes.
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* Params form */}
            {selectedTemplate && Object.keys(selectedTemplate.params_schema || {}).length > 0 && (
              <div className="space-y-3 pt-1">
                <div className="text-xs font-medium text-gray-700">Parameters</div>
                {Object.entries(selectedTemplate.params_schema).map(([key, schema]) => (
                  <ParamField
                    key={key}
                    name={key}
                    schema={schema}
                    value={params[key]}
                    onChange={(v) => setParams((p) => ({ ...p, [key]: v }))}
                  />
                ))}
              </div>
            )}
            </div>
            )}

            {/* ── Custom Command Mode ──────────────────────────────── */}
            {execSource === 'custom' && (
            <div className="space-y-4" data-testid="custom-command-panel">

              {/* Method + Endpoint */}
              <div className="flex gap-2 items-end">
                <div className="w-32 flex-shrink-0">
                  <label className="text-xs font-medium text-gray-700 mb-1 block">HTTP Method</label>
                  <Select value={cmdMethod} onValueChange={setCmdMethod}>
                    <SelectTrigger data-testid="cmd-method-select" className="w-full font-mono text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                        <SelectItem key={m} value={m}><span className="font-mono">{m}</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Endpoint</label>
                  <Input
                    value={cmdEndpoint}
                    onChange={(e) => setCmdEndpoint(e.target.value)}
                    placeholder="/api/admin/payout-migration/preview"
                    className="font-mono text-sm"
                    data-testid="cmd-endpoint-input"
                  />
                  {cmdEndpoint && !cmdEndpoint.startsWith('/api/') && (
                    <p className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Endpoint must start with /api/
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  title="Save this command"
                  onClick={saveCurrentCmd}
                  data-testid="cmd-save-btn"
                  className="flex-shrink-0"
                >
                  <Star className="h-4 w-4 text-amber-500" />
                </Button>
              </div>

              {/* Request Body — hidden for GET */}
              {cmdMethod !== 'GET' && (
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 flex items-center justify-between">
                    <span>Request Body <span className="text-gray-400 font-normal">(JSON)</span></span>
                    <span className="text-gray-400">leave empty if not needed</span>
                  </label>
                  <textarea
                    value={cmdBody}
                    onChange={(e) => { setCmdBody(e.target.value); setCmdBodyError(''); }}
                    placeholder={'{\n  "vendor_id": "...",\n  "dry_run": true\n}'}
                    rows={5}
                    className={`w-full font-mono text-sm rounded-md border px-3 py-2 bg-gray-50 resize-y focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                      cmdBodyError ? 'border-red-400' : 'border-gray-200'
                    }`}
                    data-testid="cmd-body-textarea"
                    spellCheck={false}
                  />
                  {cmdBodyError && (
                    <p className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> {cmdBodyError}
                    </p>
                  )}
                </div>
              )}

              {/* Execution Mode */}
              <div>
                <label className="text-xs font-medium text-gray-700 mb-2 block">Execution Mode</label>
                <div className="flex gap-2 flex-wrap" data-testid="cmd-exec-mode-group">
                  {[
                    { id: 'preview',  label: 'Preview',  desc: cmdMethod === 'GET' ? 'Run GET request' : 'Injects dry_run=true', cls: 'border-blue-200 text-blue-700 bg-blue-50' },
                    { id: 'dry_run',  label: 'Dry Run',  desc: 'Injects dry_run=true',  cls: 'border-amber-200 text-amber-700 bg-amber-50' },
                    { id: 'execute',  label: 'Execute',  desc: 'Injects dry_run=false — writes DB', cls: 'border-rose-200 text-rose-700 bg-rose-50' },
                  ].map(({ id, label, desc, cls }) => (
                    <button
                      key={id}
                      onClick={() => setCmdExecMode(id)}
                      data-testid={`cmd-mode-${id}`}
                      className={`flex-1 min-w-[100px] rounded-md border px-3 py-2 text-left transition-all ${
                        cmdExecMode === id ? `${cls} font-semibold shadow-sm` : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-medium">{label}</div>
                      <div className="text-xs mt-0.5 opacity-75">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Validation summary */}
              {cmdExecMode === 'execute' && (
                <div className="flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>Execute mode will write to the database. You will be asked to type CONFIRM before proceeding.</span>
                </div>
              )}

              {/* Run button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleRun}
                  disabled={!cmdEndpoint.trim() || running}
                  data-testid="cmd-run-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
                  {running ? 'Running…' : `Run${cmdExecMode === 'execute' ? ' (Execute)' : ''}`}
                </Button>
              </div>
            </div>
            )}

          </CardContent>
        </Card>

        {/* ── Saved Commands ───────────────────────────────────────────── */}
        {savedCmds.length > 0 && (
          <Card className="border-amber-100">
            <CardHeader className="pb-2">
              <button
                className="flex items-center justify-between w-full text-left"
                onClick={() => setSavedOpen((v) => !v)}
                data-testid="saved-cmds-toggle"
              >
                <CardTitle className="flex items-center gap-2 text-sm font-semibold text-amber-700">
                  <Star className="h-4 w-4" /> Saved Commands
                  <span className="text-xs text-amber-500 font-normal">({savedCmds.length})</span>
                </CardTitle>
                {savedOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </button>
            </CardHeader>
            {savedOpen && (
              <CardContent className="pt-0">
                <div className="space-y-1.5">
                  {savedCmds.map((cmd) => (
                    <div
                      key={cmd.id}
                      className="flex items-center gap-2 rounded-md border border-gray-100 px-3 py-2 hover:bg-amber-50/40 group"
                    >
                      <span className={`text-[10px] font-bold font-mono px-1.5 py-0.5 rounded ${
                        cmd.method === 'GET' ? 'bg-blue-100 text-blue-700' :
                        cmd.method === 'POST' ? 'bg-green-100 text-green-700' :
                        cmd.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{cmd.method}</span>
                      <span className="font-mono text-xs text-gray-700 flex-1 truncate">{cmd.endpoint}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs h-7 px-2 opacity-0 group-hover:opacity-100"
                        onClick={() => loadSavedCmd(cmd)}
                        data-testid={`load-saved-cmd-${cmd.id}`}
                      >
                        Load
                      </Button>
                      <button
                        onClick={() => deleteSavedCmd(cmd.id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"
                        data-testid={`delete-saved-cmd-${cmd.id}`}
                        title="Remove shortcut"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Last run result */}
        {lastResult && (
          <Card data-testid="maintenance-last-result-card">
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-base flex-wrap">
                  Last run — {lastResult._template_name || selectedTemplate?.name}
                  <StatusBadge status={lastResult.status} summary={lastResult.summary || lastResult?.result?.summary} />
                  {(lastResult.run_source === 'custom_command' || execSource === 'custom') && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                      <Terminal className="h-2.5 w-2.5" /> Custom Command
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="font-mono text-[11px] mt-1">
                  run_id: {lastResult.run_id}
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600">
                  {_summaryLine(lastResult.summary) || '—'}
                </div>
                {/* iter-252-AE — D-080 dry-run report downloads. Surfaces ONLY
                    when the executor returns `report_markdown` / `report_json`
                    (currently the Payout Impact Dry-Run template). Generic for
                    any future template that needs a downloadable artefact. */}
                {lastResult?.result?.report_markdown && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => _downloadBlob(
                      lastResult.result.report_markdown,
                      `${lastResult._template_name || 'report'}-${lastResult.run_id || 'run'}.md`,
                      'text/markdown',
                    )}
                    data-testid="maintenance-download-md-btn"
                  >
                    Download .md
                  </Button>
                )}
                {lastResult?.result?.report_json && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => _downloadBlob(
                      JSON.stringify(lastResult.result.report_json, null, 2),
                      `${lastResult._template_name || 'report'}-${lastResult.run_id || 'run'}.json`,
                      'application/json',
                    )}
                    data-testid="maintenance-download-json-btn"
                  >
                    Download .json
                  </Button>
                )}
                {lastResult?._template_id === FR_RECON_TEMPLATE_ID && (
                  <ReconciliationLogsButton apiCall={apiCall} />
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* iter-373b — Generic category-count chips: any template's
                  summary can opt in by returning a `_category_summary`
                  array of {key,label,count} — rendered as colored chips
                  above the raw rows table for an at-a-glance breakdown. */}
              <CategorySummaryChips
                categories={
                  lastResult?.result?.summary?._category_summary
                  || lastResult?.summary?._category_summary
                }
              />
              {/* Detect payout migration preview response by its summary shape */}
              {(lastResult?.summary?.total_previewed != null &&
                Array.isArray(lastResult?.result?.rows) &&
                lastResult?.result?.rows[0]?.vendor_name != null) ? (
                <PayoutMigrationTable
                  rows={lastResult.result.rows}
                  summary={lastResult.summary}
                />
              ) : (lastResult?._template_id === FR_RECON_TEMPLATE_ID) ? (
                <ReconciliationFixTable
                  rows={lastResult?.result?.rows || []}
                  month={params?.month}
                  apiCall={apiCall}
                  onFixed={() => toast.message('Row updated — re-run the template to refresh the full report.')}
                />
              ) : (
                <ResultTable rows={lastResult?.result?.rows || []} />
              )}
            </CardContent>
          </Card>
        )}

        {/* iter-230q — Async batch live progress card */}
        {activeAsyncRun && activeAsyncRun.status === 'running' && (
          <Card data-testid="maintenance-async-progress-card" className="border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Zap className="h-4 w-4 text-purple-600" /> Async batch running
                <StatusBadge status="running" />
              </CardTitle>
              <CardDescription className="font-mono text-[11px]">
                run_id: {activeAsyncRun.run_id}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-gray-700">
                  <span>
                    Progress: {activeAsyncRun.progress?.processed ?? 0} / {activeAsyncRun.progress?.total ?? '—'}
                  </span>
                  <span className="font-mono">
                    {activeAsyncRun.progress?.percent != null ? `${activeAsyncRun.progress.percent}%` : '—'}
                  </span>
                </div>
                <div className="h-2 w-full bg-gray-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-purple-500 transition-all duration-500"
                    style={{ width: `${activeAsyncRun.progress?.percent ?? 0}%` }}
                    data-testid="async-progress-bar"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Polling /runs/{activeAsyncRun.run_id} every 3 seconds. You can leave this page — the run continues in the background.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Recent runs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="h-4 w-4" /> Recent runs
              </CardTitle>
              <CardDescription>Last 20 runs across all templates.</CardDescription>
            </div>
            {loadingRuns && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </CardHeader>
          <CardContent>
            {recentRuns.length === 0 ? (
              <div className="text-sm text-gray-500 py-6 text-center">No runs yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Triggered by</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Summary</TableHead>
                    <TableHead>Started</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentRuns.map((r) => (
                    <TableRow key={r.id} data-testid={`run-row-${r.id}`}>
                      <TableCell className="font-medium max-w-xs">
                        <span className="truncate block font-mono text-xs">{r.template_name}</span>
                      </TableCell>
                      <TableCell>
                        {r.run_source === 'custom_command' ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded">
                            <Terminal className="h-2.5 w-2.5" /> Custom
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">
                            <Wrench className="h-2.5 w-2.5" /> Template
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{r.triggered_by?.email || '—'}</TableCell>
                      <TableCell><StatusBadge status={r.status} summary={r.result_summary} /></TableCell>
                      <TableCell className="text-xs text-gray-600">{_summaryLine(r.result_summary) || '—'}</TableCell>
                      <TableCell className="text-xs text-gray-500">{formatISTTimestamp(r.started_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* iter-230q — Confirmation modal for mutating apply (dry_run=false) */}
        {/* Confirmation modal — shared: templates + custom execute mode */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent data-testid="maintenance-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                {execSource === 'custom'
                  ? `Confirm Execute — ${cmdMethod} ${cmdEndpoint}`
                  : `Confirm apply — ${selectedTemplate?.name}`
                }
              </DialogTitle>
              <DialogDescription className="space-y-2 pt-2">
                {execSource === 'custom' ? (
                  <>
                    <span className="block">
                      You are about to run <code className="mx-1 px-1.5 py-0.5 bg-indigo-50 text-indigo-800 rounded font-mono">{cmdMethod} {cmdEndpoint}</code> in <strong>Execute mode</strong>.
                    </span>
                    <span className="block text-sm text-rose-700 font-medium">
                      dry_run=false will be injected. This may write to the database.
                    </span>
                  </>
                ) : (
                  <span className="block">
                    You are about to run <strong>{selectedTemplate?.name}</strong> with
                    <code className="mx-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">dry_run: false</code>.
                    This will write to the production database.
                  </span>
                )}
                <span className="block text-sm text-gray-600">
                  Type <strong>CONFIRM</strong> below to proceed. Anything else cancels safely.
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="py-3">
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type CONFIRM"
                autoFocus
                data-testid="maintenance-confirm-input"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setConfirmOpen(false); setConfirmText(''); }}
                data-testid="maintenance-confirm-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (confirmText.trim() === 'CONFIRM') {
                    setConfirmOpen(false);
                    setConfirmText('');
                    if (execSource === 'custom') {
                      executeCustomRun();
                    } else {
                      executeRun();
                    }
                  } else {
                    toast.error('Confirmation did not match', {
                      description: 'You must type CONFIRM (all caps) to apply.',
                    });
                  }
                }}
                disabled={confirmText.trim() !== 'CONFIRM'}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="maintenance-confirm-apply-btn"
              >
                {execSource === 'custom' ? 'Execute' : 'Apply changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function _seedParams(template) {
  const out = {};
  Object.entries(template?.params_schema || {}).forEach(([key, schema]) => {
    if (schema && Object.prototype.hasOwnProperty.call(schema, 'default')) {
      out[key] = schema.default;
    }
  });
  return out;
}

// iter-252-AE — Browser file download helper. Used by the Maintenance
// Runner result card when a template returns `report_markdown` /
// `report_json` artefacts (e.g. Payout Impact Dry-Run).
function _downloadBlob(text, filename, mime) {
  try {
    const blob = new Blob([text], { type: mime || 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download.txt';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  } catch (e) {
    // Last-resort fallback for environments without Blob support — just
    // open a data URL in a new tab so the admin can copy-paste.
    const dataUrl = `data:${mime || 'text/plain'};charset=utf-8,${encodeURIComponent(text)}`;
    window.open(dataUrl, '_blank');
  }
}

function _summaryLine(summary) {
  if (!summary || typeof summary !== 'object') return '';
  return Object.entries(summary)
    .filter(([k]) => !k.startsWith('_'))  // hide internal keys like _row_count
    .map(([k, v]) => {
      // iter-230bq — Render arrays as "N items" instead of stringifying
      // (which produces "[object Object],[object Object],..."). The full
      // table below renders rows correctly. Long strings get truncated.
      let displayV;
      if (Array.isArray(v)) {
        displayV = `${v.length} items`;
      } else if (v && typeof v === 'object') {
        displayV = JSON.stringify(v).slice(0, 80);
      } else {
        const s = String(v ?? '');
        displayV = s.length > 200 ? s.slice(0, 200) + '…' : s;
      }
      return `${k.replace(/_/g, ' ')}: ${displayV}`;
    })
    .join(' · ');
}

// iter-373b — Colors/icons for well-known `issue` values used by the
// Finance Register vs Commission Invoice reconciliation template (and any
// future template that reuses this vocabulary). Unknown values fall back
// to a plain neutral badge with a humanized label — this stays generic.
const ISSUE_BADGE_STYLE = {
  matched: { icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  missing_from_invoice: { icon: AlertTriangle, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  invoiced_in_wrong_month: { icon: AlertTriangle, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  commission_amount_mismatch: { icon: AlertTriangle, cls: 'bg-red-50 text-red-700 border-red-200' },
  commission_rate_error_at_settlement: { icon: AlertTriangle, cls: 'bg-red-50 text-red-700 border-red-200' },
  duplicate_invoice: { icon: AlertTriangle, cls: 'bg-red-50 text-red-700 border-red-200' },
  rate_changed_since_settlement: { icon: Info, cls: 'bg-blue-50 text-blue-700 border-blue-200' },
};

function _humanizeIssue(v) {
  return String(v).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function IssueBadge({ value }) {
  const style = ISSUE_BADGE_STYLE[value] || { icon: null, cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  const Icon = style.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${style.cls}`}
      data-testid={`issue-badge-${value}`}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {_humanizeIssue(value)}
    </span>
  );
}

// iter-373b — Generic count-chip row. Any executor summary can opt in via
// a `_category_summary: [{key,label,count}]` array in its returned summary.
function CategorySummaryChips({ categories }) {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="category-summary-chips">
      {categories.map((c) => (
        <span
          key={c.key}
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
            (ISSUE_BADGE_STYLE[c.key] || {}).cls || 'bg-gray-50 text-gray-600 border-gray-200'
          }`}
          data-testid={`category-chip-${c.key}`}
        >
          {c.label}: <span className="font-bold">{c.count}</span>
        </span>
      ))}
    </div>
  );
}

// iter-377 — "View reconciliation audit logs" for the FR-vs-Invoice fix flow.
function ReconciliationLogsButton({ apiCall }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/commission-invoices/audit/fr-vs-invoice-fix/logs?limit=100');
      setLogs(res?.logs || []);
    } catch (e) {
      toast.error('Could not load reconciliation logs', { description: e?.message || String(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={load} data-testid="view-reconciliation-logs-btn">
        <ScrollText className="h-4 w-4 mr-2" /> Audit Logs
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="reconciliation-logs-dialog">
          <DialogHeader>
            <DialogTitle>Reconciliation fix — audit logs</DialogTitle>
            <DialogDescription>Most recent 100 fixes/reverts applied via this template.</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : logs.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">No fixes applied yet.</div>
          ) : (
            <div className="space-y-2" data-testid="reconciliation-logs-list">
              {logs.map((l) => (
                <div key={l.id} className="rounded-md border border-gray-100 p-2.5 text-xs" data-testid={`reconciliation-log-${l.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-800">{l.description}</span>
                    <span className="text-gray-400 whitespace-nowrap">{formatISTTimestamp(l.created_at)}</span>
                  </div>
                  <div className="text-gray-500 mt-0.5">{l.admin_email} · order {l.target_id}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// iter-377 — Makes the FR-vs-Commission-Invoice reconciliation template
// actionable: per-row checkboxes + Fix action (with a Fix Invoice / Fix
// Finance Register choice for `invoiced_in_wrong_month`), plus a bulk
// "Fix Selected" flow with a dry-run preview before committing.
function ReconciliationFixTable({ rows, month, apiCall, onFixed }) {
  const [selected, setSelected] = useState(() => new Set());
  const [fixTypeByOrder, setFixTypeByOrder] = useState({});
  const [rowStatus, setRowStatus] = useState({}); // order_id -> 'fixed' | 'failed'
  const [busyOrder, setBusyOrder] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewResults, setPreviewResults] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  if (!rows || rows.length === 0) {
    return <div className="text-sm text-gray-500 py-4">No drift rows returned — everything matches.</div>;
  }

  const buildItem = (row, overrideFixType) => ({
    order_id: row.order_id,
    issue: row.issue,
    vendor_id: row.vendor_id,
    month: month || '',
    fix_type: row.issue === 'invoiced_in_wrong_month' ? (overrideFixType || fixTypeByOrder[row.order_id] || 'fix_invoice') : undefined,
    invoice_number: row.invoice_number,
    ledger_commission: row.issue === 'commission_rate_error_at_settlement' ? row.finance_register_commission_today : row.ledger_commission_actually_paid,
    ledger_commission_gst: row.issue === 'commission_rate_error_at_settlement' ? row.finance_register_commission_gst_today : row.ledger_commission_gst_actually_paid,
  });

  const applyFixes = async (items, dryRun) => {
    return apiCall('/api/admin/commission-invoices/audit/fr-vs-invoice-fix', {
      method: 'POST',
      body: JSON.stringify({ items, dry_run: dryRun }),
    });
  };

  const runSingleFix = async (row) => {
    if (!window.confirm(`Apply the fix for order ${row.order_number || row.order_id}? This writes to the database.`)) return;
    setBusyOrder(row.order_id);
    try {
      const res = await applyFixes([buildItem(row)], false);
      const r = res?.results?.[0];
      if (r?.ok) {
        setRowStatus((s) => ({ ...s, [row.order_id]: 'fixed' }));
        toast.success('Row fixed', { description: r.outcome || r.action });
        onFixed && onFixed();
      } else {
        setRowStatus((s) => ({ ...s, [row.order_id]: 'failed' }));
        toast.error('Fix failed', { description: r?.outcome || r?.error || 'Unknown error' });
      }
    } catch (e) {
      toast.error('Fix failed', { description: e?.message || String(e) });
    } finally {
      setBusyOrder(null);
    }
  };

  const fixableRows = rows.filter((r) => FR_FIXABLE_ISSUES.includes(r.issue) && rowStatus[r.order_id] !== 'fixed');
  const selectedRows = fixableRows.filter((r) => selected.has(r.order_id));

  const toggleRow = (orderId, checked) => {
    setSelected((s) => {
      const next = new Set(s);
      if (checked) next.add(orderId); else next.delete(orderId);
      return next;
    });
  };

  const openBulkPreview = async () => {
    if (selectedRows.length === 0) return;
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await applyFixes(selectedRows.map((r) => buildItem(r)), true);
      setPreviewResults(res?.results || []);
    } catch (e) {
      toast.error('Preview failed', { description: e?.message || String(e) });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmBulkApply = async () => {
    setApplying(true);
    try {
      const res = await applyFixes(selectedRows.map((r) => buildItem(r)), false);
      const results = res?.results || [];
      const nextStatus = {};
      results.forEach((r) => { nextStatus[r.order_id] = r.ok ? 'fixed' : 'failed'; });
      setRowStatus((s) => ({ ...s, ...nextStatus }));
      setSelected(new Set());
      setPreviewOpen(false);
      toast.success(`Applied ${res?.summary?.applied || 0} of ${res?.summary?.total || 0} fixes`, {
        description: res?.summary?.failed ? `${res.summary.failed} failed — see rows for details.` : undefined,
      });
      onFixed && onFixed();
    } catch (e) {
      toast.error('Bulk apply failed', { description: e?.message || String(e) });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="reconciliation-fix-table">
      {selectedRows.length > 0 && (
        <div className="flex items-center justify-between rounded-md bg-indigo-50 border border-indigo-200 px-3 py-2">
          <span className="text-xs text-indigo-800 font-medium">{selectedRows.length} row(s) selected</span>
          <Button size="sm" onClick={openBulkPreview} data-testid="bulk-fix-selected-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Fix Selected ({selectedRows.length})
          </Button>
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Issue</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Order</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Vendor</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Details</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Fix</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 200).map((row, idx) => {
              const isFixable = FR_FIXABLE_ISSUES.includes(row.issue);
              const status = rowStatus[row.order_id];
              return (
                <TableRow key={row.order_id || idx} data-testid={`recon-row-${row.order_id}`}>
                  <TableCell>
                    {isFixable && status !== 'fixed' && (
                      <Checkbox
                        checked={selected.has(row.order_id)}
                        onCheckedChange={(v) => toggleRow(row.order_id, !!v)}
                        data-testid={`recon-row-checkbox-${row.order_id}`}
                      />
                    )}
                  </TableCell>
                  <TableCell><IssueBadge value={row.issue} /></TableCell>
                  <TableCell className="text-xs font-mono">{row.order_number || row.order_id?.slice(0, 8)}</TableCell>
                  <TableCell className="text-xs max-w-[140px] truncate" title={row.vendor_name}>{row.vendor_name || '—'}</TableCell>
                  <TableCell className="text-xs text-gray-500 max-w-[280px]">
                    {row.issue === 'missing_from_invoice' && `FR shows ₹${row.finance_register_commission ?? '—'} commission, no invoice line`}
                    {row.issue === 'invoiced_in_wrong_month' && `Invoiced ₹${row.invoiced_commission ?? '—'} on ${row.invoice_number || '—'}`}
                    {row.issue === 'commission_amount_mismatch' && `Ledger ₹${row.ledger_commission_actually_paid ?? '—'} vs invoiced ₹${row.invoiced_commission ?? '—'} (${row.correction_needed === 'manual_credit_debit_note_pair' ? 'invoice sent' : 'not sent'})`}
                    {row.issue === 'commission_rate_error_at_settlement' && `Historically-correct ₹${row.finance_register_commission_today ?? '—'} (${row.finance_register_rate_pct ?? '—'}%) vs invoiced ₹${row.invoiced_commission ?? '—'} (${row.correction_needed === 'manual_credit_debit_note_pair' ? 'invoice sent' : 'not sent'})`}
                    {row.issue === 'duplicate_invoice' && (
                      <span className="text-red-600">{(row.invoice_numbers || []).join(', ')}</span>
                    )}
                    {row.issue === 'rate_changed_since_settlement' && 'Informational — no action needed'}
                  </TableCell>
                  <TableCell>
                    {status === 'fixed' && (
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium" data-testid={`recon-row-fixed-${row.order_id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Fixed
                      </span>
                    )}
                    {status === 'failed' && (
                      <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                        <XCircle className="h-3.5 w-3.5" /> Failed
                      </span>
                    )}
                    {!status && isFixable && (
                      <div className="flex items-center gap-1.5">
                        {row.issue === 'invoiced_in_wrong_month' && (
                          <select
                            className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white"
                            value={fixTypeByOrder[row.order_id] || 'fix_invoice'}
                            onChange={(e) => setFixTypeByOrder((s) => ({ ...s, [row.order_id]: e.target.value }))}
                            data-testid={`recon-fix-type-${row.order_id}`}
                          >
                            <option value="fix_invoice">Fix Invoice</option>
                            <option value="fix_finance_register">Fix Finance Register</option>
                          </select>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyOrder === row.order_id}
                          onClick={() => runSingleFix(row)}
                          data-testid={`recon-fix-btn-${row.order_id}`}
                        >
                          {busyOrder === row.order_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Fix'}
                        </Button>
                      </div>
                    )}
                    {!status && !isFixable && (
                      <span className="text-xs text-gray-400">
                        {row.issue === 'duplicate_invoice' ? 'Manual review' : '—'}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {rows.length > 200 && (
        <div className="text-xs text-gray-500">Showing first 200 of {rows.length} rows.</div>
      )}

      {/* Bulk dry-run preview modal */}
      <Dialog open={previewOpen} onOpenChange={(o) => { if (!applying) setPreviewOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bulk-fix-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview — {selectedRows.length} fix(es)</DialogTitle>
            <DialogDescription>Nothing has been written yet. Review the predicted action per row, then confirm.</DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : (
            <div className="space-y-1.5" data-testid="bulk-fix-preview-list">
              {previewResults.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-xs rounded-md border border-gray-100 px-2.5 py-1.5">
                  <span className="font-mono">{r.order_id?.slice(0, 8)}</span>
                  <IssueBadge value={r.issue} />
                  <span className="text-gray-600">{(r.predicted_action || '').replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={applying} data-testid="bulk-fix-preview-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={confirmBulkApply}
              disabled={applying || previewLoading}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="bulk-fix-preview-confirm-btn"
            >
              {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirm &amp; Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// iter-373b — Searchable "All Vendors" + specific-vendor dropdown, resolved
// entirely client-side (fetches the existing admin vendor list once). Kept
// as its own field type ("vendor_select") rather than a static params_schema
// enum since the vendor list is data, not config, and can be large.
function VendorSelectField({ value, onChange, testId }) {
  const { apiCall } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    apiCall('/api/vendors/?limit=1000')
      .then((data) => {
        if (mounted) setVendors(Array.isArray(data) ? data : (data?.vendors || []));
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [apiCall]);

  return (
    <Select value={value || '__all__'} onValueChange={(v) => onChange(v === '__all__' ? '' : v)}>
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder={loading ? 'Loading vendors…' : 'All Vendors'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">All Vendors</SelectItem>
        {vendors.map((v) => (
          <SelectItem key={v.id} value={v.id}>
            {v.store_name || v.name || v.email}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ParamField({ name, schema, value, onChange }) {
  const label = schema.label || name;
  const help = schema.help;
  if (schema.type === 'vendor_select') {
    return (
      <div className="space-y-1.5" data-testid={`param-field-${name}`}>
        <Label htmlFor={`param-${name}`}>{label}</Label>
        <VendorSelectField value={value} onChange={onChange} testId={`param-${name}`} />
        {help && <p className="text-xs text-gray-500">{help}</p>}
      </div>
    );
  }
  if (schema.type === 'bool') {
    return (
      <div className="flex items-start gap-3">
        <Switch
          id={`param-${name}`}
          checked={!!value}
          onCheckedChange={onChange}
          data-testid={`param-${name}-switch`}
        />
        <div>
          <label htmlFor={`param-${name}`} className="text-sm font-medium text-gray-800 cursor-pointer">
            {label}
          </label>
          {help && <p className="text-xs text-gray-500 mt-0.5 max-w-prose">{help}</p>}
        </div>
      </div>
    );
  }
  if (schema.type === 'int') {
    return (
      <div>
        <label className="text-sm font-medium text-gray-800 block mb-1">{label}</label>
        <input
          type="number"
          className="w-32 rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
          data-testid={`param-${name}-input`}
        />
        {help && <p className="text-xs text-gray-500 mt-0.5">{help}</p>}
      </div>
    );
  }
  // iter-236 — `select` renderer for enum-style params (e.g. the
  // collision-audit template's `mode` ∈ {audit, plan, execute}).
  // Without this branch the field fell through to a text input,
  // which let users type free-form values that the executor then
  // rejected silently.
  if (schema.type === 'select') {
    const options = Array.isArray(schema.options) ? schema.options : [];
    return (
      <div>
        <label className="text-sm font-medium text-gray-800 block mb-1">{label}</label>
        <select
          className="w-48 rounded-md border border-gray-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`param-${name}-select`}
        >
          {options.map((opt) => {
            const v = typeof opt === 'object' ? opt.value : opt;
            const l = typeof opt === 'object' ? (opt.label || opt.value) : opt;
            return <option key={v} value={v}>{l}</option>;
          })}
        </select>
        {help && <p className="text-xs text-gray-500 mt-0.5">{help}</p>}
      </div>
    );
  }
  // iter-236 — `string_array` renderer for comma-separated lists
  // (e.g. the collision-audit template's `listing_ids` whitelist).
  // We parse on blur into a clean array of trimmed non-empty
  // tokens; empty input ⇒ empty array, matching the executor's
  // expectation of `params.listing_ids: [str]`.
  if (schema.type === 'string_array') {
    const display = Array.isArray(value) ? value.join(', ') : (value ?? '');
    return (
      <div>
        <label className="text-sm font-medium text-gray-800 block mb-1">{label}</label>
        <input
          type="text"
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          value={display}
          placeholder="uuid-1, uuid-2, uuid-3"
          onChange={(e) => {
            // Parse on every change so the parent state matches the
            // visible input — split on comma OR whitespace, drop empties.
            const tokens = e.target.value
              .split(/[\s,]+/)
              .map((t) => t.trim())
              .filter(Boolean);
            onChange(tokens);
          }}
          data-testid={`param-${name}-input`}
        />
        {help && <p className="text-xs text-gray-500 mt-0.5">{help}</p>}
      </div>
    );
  }
  // Default: string text input.
  return (
    <div>
      <label className="text-sm font-medium text-gray-800 block mb-1">{label}</label>
      <input
        type="text"
        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`param-${name}-input`}
      />
      {help && <p className="text-xs text-gray-500 mt-0.5">{help}</p>}
    </div>
  );
}

// ── Payout Migration Preview Table ──────────────────────────────────────────
// Renders the enriched /api/admin/payout-migration/preview response as an
// approval-ready report with human-readable vendor/order identifiers and
// computed commission/payout deltas.

const STATUS_CELL_CFG = {
  DRY_RUN:      'bg-blue-50 text-blue-700 border-blue-200',
  APPLIED:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  MISSING_PLAN: 'bg-amber-50 text-amber-700 border-amber-200',
  SKIPPED:      'bg-gray-50 text-gray-500 border-gray-200',
};

function DeltaCell({ v }) {
  if (v == null) return <span className="text-gray-300">—</span>;
  const n = Number(v);
  if (Math.abs(n) < 0.005) return <span className="text-gray-400 font-mono text-xs">₹0.00</span>;
  const cls = n > 0 ? 'text-emerald-600' : 'text-red-600';
  return <span className={`font-mono text-xs font-semibold ${cls}`}>{n > 0 ? '+' : ''}₹{Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
}

function INR({ v }) {
  if (v == null || v === 0) return <span className="text-gray-300 text-xs">—</span>;
  return <span className="font-mono text-xs">₹{Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
}

function PCT({ v }) {
  if (v == null) return <span className="text-gray-300 text-xs">—</span>;
  return <span className="font-mono text-xs">{Number(v).toFixed(2)}%</span>;
}

function PayoutMigrationTable({ rows, summary }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-gray-500 py-4">No records to preview.</div>;
  }
  const missingPlan = rows.filter(r => r.status === 'MISSING_PLAN');
  const actionable  = rows.filter(r => r.status !== 'MISSING_PLAN' && r.status !== 'SKIPPED');

  return (
    <div className="space-y-4" data-testid="payout-migration-table">
      {/* Summary strip */}
      <div className="flex flex-wrap gap-3 text-xs">
        {[
          { label: 'Total Previewed',  val: summary?.total_previewed,  cls: 'bg-gray-50 text-gray-700' },
          { label: 'Would Change',     val: summary?.would_change,      cls: 'bg-blue-50 text-blue-700' },
          { label: 'Missing Plan',     val: summary?.missing_plan,      cls: 'bg-amber-50 text-amber-700' },
          { label: 'Skipped',          val: summary?.skipped,           cls: 'bg-gray-50 text-gray-500' },
          ...(summary?.fallback_used > 0 ? [{ label: 'Fallback Plan Used', val: summary.fallback_used, cls: 'bg-orange-50 text-orange-700' }] : []),
        ].map(({ label, val, cls }) => (
          <span key={label} className={`px-3 py-1 rounded-full border font-medium ${cls}`}>
            {label}: {val ?? 0}
          </span>
        ))}
        {summary?.total_net_delta != null && (
          <span className={`px-3 py-1 rounded-full border font-semibold font-mono ${
            summary.total_net_delta < 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
          }`}>
            Net Δ: {summary.total_net_delta > 0 ? '+' : ''}₹{Number(summary.total_net_delta).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </span>
        )}
      </div>

      {/* Missing plan alert */}
      {missingPlan.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-800 mb-1">
            {missingPlan.length} vendor{missingPlan.length > 1 ? 's' : ''} missing subscription plan — cannot be migrated
          </p>
          <div className="flex flex-wrap gap-2">
            {missingPlan.map((r, i) => (
              <span key={i} className="text-xs bg-white border border-amber-200 rounded px-2 py-0.5 text-amber-700">
                {r.vendor_name || r.vendor_id?.slice(0, 8)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Main approval table */}
      {actionable.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-xs font-semibold whitespace-nowrap">Status</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Aniyor Order</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Shopify #</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Parent</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Vendor</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Date</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Gross</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Old Plan → New Plan</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Comm %</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Comm ₹</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Old Net</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">New Net</TableHead>
                <TableHead className="text-xs font-semibold whitespace-nowrap">Net Δ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {actionable.map((row, idx) => {
                const statusCls = STATUS_CELL_CFG[row.status] || 'bg-gray-50 text-gray-500 border-gray-200';
                const planChanged = row.old_plan !== row.new_plan;
                return (
                  <TableRow key={row.order_id || idx} className="hover:bg-gray-50 transition-colors">
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border whitespace-nowrap ${statusCls}`}>
                        {row.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs font-mono font-medium text-gray-900 whitespace-nowrap">
                      {row.aniyor_order || row.order_number || row.order_id?.slice(0, 8) || '—'}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-gray-500 whitespace-nowrap">
                      {row.shopify_order_id !== '—' ? row.shopify_order_id : '—'}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-gray-500 whitespace-nowrap">
                      {row.parent_order !== '—' ? row.parent_order : '—'}
                    </TableCell>
                    <TableCell className="text-xs font-medium text-gray-900 whitespace-nowrap max-w-[160px] truncate" title={row.vendor_name}>
                      {row.vendor_name || '—'}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500 whitespace-nowrap">{row.order_date || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap"><INR v={row.gross_amount} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <span className="text-gray-500">{row.old_plan || '—'}</span>
                      {planChanged && (
                        <>
                          <span className="text-gray-300 mx-1">→</span>
                          <span className="text-indigo-700 font-medium">{row.new_plan || '—'}</span>
                        </>
                      )}
                      {!planChanged && <span className="text-gray-300 ml-1">(unchanged)</span>}
                      {row.fallback_plan_used && (
                        <span className="ml-1 px-1 py-0.5 bg-orange-100 text-orange-700 text-[10px] rounded border border-orange-200" title="Current plan used as fallback — no subscription existed on order date">fallback</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className="text-xs text-gray-500"><PCT v={row.old_commission_pct} /></span>
                      <span className="text-gray-300 mx-1">→</span>
                      <span className="text-xs font-medium text-gray-900"><PCT v={row.new_commission_pct} /></span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className="text-xs text-gray-400 line-through"><INR v={row.old_commission_amt} /></span>
                      <span className="ml-1"><INR v={row.new_commission_amt} /></span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap"><INR v={row.old_net_payout} /></TableCell>
                    <TableCell className="whitespace-nowrap"><INR v={row.new_net_payout} /></TableCell>
                    <TableCell className="whitespace-nowrap"><DeltaCell v={row.net_payout_delta} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {rows.length > 200 && (
        <div className="text-xs text-gray-500">Showing first 200 of {rows.length} records.</div>
      )}
    </div>
  );
}

function ResultTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-gray-500 py-4">No rows returned.</div>;
  }
  // Infer columns from the first row, preferring identifying fields up front.
  // `issue` goes first when present so an admin scanning a reconciliation
  // report immediately sees what class of drift each row is.
  const preferred = ['issue', 'id', 'order_id', 'order_number', 'vendor_id', 'vendor_name', 'title', 'name', 'email'];
  const allKeys = Object.keys(rows[0]);
  const cols = [
    ...preferred.filter((k) => allKeys.includes(k)),
    ...allKeys.filter((k) => !preferred.includes(k)),
  ].slice(0, 8);

  return (
    <div className="overflow-x-auto" data-testid="result-table">
      <Table>
        <TableHeader>
          <TableRow>
            {cols.map((c) => (
              <TableHead key={c} className="text-xs uppercase tracking-wider">{c}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.slice(0, 200).map((row, idx) => (
            <TableRow key={row.id || idx}>
              {cols.map((c) => (
                <TableCell key={c} className="text-xs">
                  {c === 'issue' && row[c] ? <IssueBadge value={row[c]} /> : _formatCell(row[c])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 200 && (
        <div className="text-xs text-gray-500 mt-2">Showing first 200 of {rows.length} rows.</div>
      )}
    </div>
  );
}

function _formatCell(v) {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 80);
  return String(v).slice(0, 80);
}
